/**
 * @file MobileCarouselLayout.jsx
 * @description Mobil fullscreen layout bileşeni
 *
 * TripAdvisor tarzı mobil deneyim sunar. Tam ekran harita üzerinde
 * yatay kaydırılabilir ilan karusel'i. Harita marker'ları ile
 * karusel senkronize çalışır.
 *
 * @module widget/components/layouts/MobileCarouselLayout
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { MapPane } from "../MapPane";
import { CarouselCard } from "../CarouselCard";
import { ListingDetailView } from "../ListingDetailView";
import { MapEmptyState } from "../MapEmptyState";
import { LAYOUT, TIMEOUTS } from "../../utils/constants";

/** @constant {Function} Boş fonksiyon placeholder */
const noop = () => {};

/**
 * Karusel scroll stili (CSS-in-JS)
 * @constant
 */
const carouselStyle = {
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorX: "contain",
};

/**
 * Mobil Karusel Layout Bileşeni
 *
 * Mobil cihazlar için optimize edilmiş fullscreen layout.
 * Harita ~%70, karusel ~%25, composer alanı ~%5 oranında
 * ekranı paylaşır. Harita marker'ı tıklandığında karusel
 * ilgili karta kaydırılır.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object[]} props.listings - İlan listesi
 * @param {boolean} props.isDark - Karanlık mod
 * @param {string|number} props.containerHeight - Container yüksekliği
 * @param {Function} props.onDetailClick - Detay açma callback'i
 * @param {number|null} props.selectedListingId - Seçili ilan ID'si
 * @param {Object|null} props.listingDetail - İlan detay verisi
 * @param {boolean} props.detailLoading - Detay yükleniyor
 * @param {number|null} props.regionAveragePrice - Bölge fiyat ortalaması
 * @param {number} props.selectedImageIndex - Seçili görsel indeksi
 * @param {Function} props.setSelectedImageIndex - Görsel seçim setter
 * @param {Function} props.onCloseDetail - Detay kapatma callback'i
 * @param {Function} props.onMapSearchInArea - Harita viewport araması
 * @param {boolean} props.isMapSearching - Arama devam ediyor
 * @param {boolean} [props.mapSearchEmpty=false] - Boş sonuç durumu
 *
 * @returns {JSX.Element} Layout bileşeni
 *
 * @example
 * <MobileCarouselLayout
 *   listings={listings}
 *   isDark={theme === 'dark'}
 *   containerHeight="100vh"
 *   onDetailClick={(id) => fetchDetail(id)}
 *   onCloseDetail={() => clearDetail()}
 *   {...otherProps}
 * />
 *
 * @remarks
 * - Karusel scroll-snap ile kart bazında durur
 * - Aktif kart haritada highlight edilir
 * - Kart tıklandığında fullscreen detay görünümüne geçer
 * - ChatGPT composer alanı için 100px çıkarılır
 */
export function MobileCarouselLayout({
  listings,
  isDark,
  containerHeight,
  onDetailClick,
  selectedListingId,
  listingDetail,
  detailLoading,
  regionAveragePrice,
  selectedImageIndex,
  setSelectedImageIndex,
  onCloseDetail,
  onMapSearchInArea,
  isMapSearching,
  mapSearchEmpty = false,
  // Props passed but not used (ChatGPT provides navigation buttons)
  // total, filters, onRemoveFilter, onEditFilter, onAddFilter, openExternal, onExitFullscreen
}) {
  // Track active card index for carousel-map sync
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const carouselRef = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Get current active listing ID based on carousel position
  const activeListingId = listings?.[activeCardIndex]?.id ?? null;

  // Scroll carousel to specific index
  const scrollToCard = useCallback((index) => {
    if (!carouselRef.current || !listings?.length) return;
    const scrollPosition = index * (LAYOUT.CARD_WIDTH + LAYOUT.CARD_GAP);

    isScrollingRef.current = true;
    carouselRef.current.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });

    // Reset scrolling flag after animation
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, TIMEOUTS.SCROLL_ANIMATION);
  }, [listings?.length]);

  // Handle carousel scroll to update active card
  const handleCarouselScroll = useCallback(() => {
    if (!carouselRef.current || isScrollingRef.current) return;

    const scrollLeft = carouselRef.current.scrollLeft;
    const newIndex = Math.round(scrollLeft / (LAYOUT.CARD_WIDTH + LAYOUT.CARD_GAP));

    if (newIndex !== activeCardIndex && newIndex >= 0 && newIndex < (listings?.length || 0)) {
      setActiveCardIndex(newIndex);
    }
  }, [activeCardIndex, listings?.length]);

  // Debounced scroll handler
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let scrollTimeout;
    const debouncedScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleCarouselScroll, TIMEOUTS.SCROLL_DEBOUNCE);
    };

    carousel.addEventListener("scroll", debouncedScroll, { passive: true });
    return () => {
      carousel.removeEventListener("scroll", debouncedScroll);
      clearTimeout(scrollTimeout);
    };
  }, [handleCarouselScroll]);

  // Handle map marker click - scroll carousel to matching card
  const handleMapMarkerSelect = useCallback((listing) => {
    if (!listing?.id || !listings?.length) return;

    const index = listings.findIndex((l) => l.id === listing.id);
    if (index !== -1 && index !== activeCardIndex) {
      setActiveCardIndex(index);
      scrollToCard(index);
    }
  }, [listings, activeCardIndex, scrollToCard]);

  // Handle card click - open detail view
  const handleCardClick = useCallback((listingId) => {
    if (listingId && onDetailClick) {
      onDetailClick(listingId);
    }
  }, [onDetailClick]);

  // Reset active index when listings change
  useEffect(() => {
    if (listings?.length && activeCardIndex >= listings.length) {
      setActiveCardIndex(0);
    }
  }, [listings, activeCardIndex]);

  // Calculate adjusted height accounting for ChatGPT composer (~100px)
  const adjustedHeight = typeof containerHeight === 'number'
    ? containerHeight - 100
    : `calc(${containerHeight} - 100px)`;

  // If a listing is selected, show fullscreen detail view
  if (selectedListingId) {
    return (
      <div
        className={`w-full flex flex-col ${isDark ? "bg-zinc-900" : "bg-white"}`}
        style={{ height: adjustedHeight, maxHeight: adjustedHeight, overflow: "auto" }}
      >
        <ListingDetailView
          detail={listingDetail}
          loading={detailLoading}
          regionAveragePrice={regionAveragePrice}
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          isDark={isDark}
          onBack={onCloseDetail}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative ${isDark ? "bg-zinc-900" : "bg-gray-50"}`}
      style={{ height: adjustedHeight, maxHeight: adjustedHeight, overflow: "hidden" }}
    >
      {/* Full-screen Map */}
      <div className="absolute inset-0">
        <MapPane
          listings={listings}
          isDark={isDark}
          fullHeight={true}
          hideHeader={true}
          selectedListingId={activeListingId}
          onSelectListing={handleMapMarkerSelect}
          onMapMove={noop}
          onSearchInArea={onMapSearchInArea}
          showSearchButton={!!onMapSearchInArea && !isMapSearching}
        />
      </div>

      {/* Carousel overlay - floating at bottom of map */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        {mapSearchEmpty ? (
          <MapEmptyState isDark={isDark} variant="banner" />
        ) : (
          <div
            ref={carouselRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 py-3"
            style={carouselStyle}
          >
            {listings?.map((listing, idx) => {
              const listingId = typeof listing?.id === "number" && Number.isFinite(listing.id)
                ? listing.id
                : null;

              return (
                <div
                  key={listingId ?? listing?.url ?? `carousel-card-${idx}`}
                  className="snap-center"
                >
                  <CarouselCard
                    listing={listing}
                    isActive={idx === activeCardIndex}
                    onClick={() => listingId && handleCardClick(listingId)}
                    isDark={isDark}
                  />
                </div>
              );
            })}

            {/* Fallback empty state (when no listings at all) */}
            {(!listings || listings.length === 0) && (
              <div className={`flex items-center justify-center w-full py-4 ${isDark ? "text-white/40" : "text-black/40"}`}>
                <span className="text-sm">Sonuç bulunamadı</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileCarouselLayout;
